import { useCallback, useRef, useState } from 'react';

// Constants
const GAMEBOX_READY_TIMEOUT = 1000; // 1 seconds
const GAMEBOX_POLL_INTERVAL = 500; // 500ms
const GAMEBOX_MAX_POLL_ATTEMPTS = 100; // we need this part for the case when retry count is more than 10 maybe open Game without gamebox
const GAMEBOX_IFRAME_ID = 'gamebox-widget';
const GAMEBOX_SESSION_KEY = 'gamebox-widget_url';
const GAMEBOX_MODULE_KEY = 'gamebox-widget_module';

// Types
export type GameBoxStatus = 'loading' | 'timeout' | 'error';

export interface IGameBoxDiagnostics {
  gdlibExists: boolean;
  gdlibInitialized: boolean;
  moduleExists: boolean;
  iframeExists: boolean;
  urlExists: boolean;
  isReady: boolean;
}

export interface IUseGameBoxReadyResult {
  isPopUpOpen: boolean;
  retryCount: number;
  status: GameBoxStatus;
  description: string;
  handleRetry: () => Promise<void>;
  handleClose: () => void;
  checkReadiness: () => boolean;
  showPopup: () => void;
}

interface IUseGameBoxReadyOptions {
  onSuccess?: () => void;
  onError?: () => void;
  getBaseDescription: () => string;
  initializeGDLib?: (force?: boolean) => Promise<void>;
}

// Utility: Get current GameBox diagnostics
export const getGameBoxDiagnostics = (): IGameBoxDiagnostics => {
  const gdlib = window.__gdlib;
  const gameboxModule = gdlib?.[GAMEBOX_MODULE_KEY];
  const gameboxIframe = document.getElementById(GAMEBOX_IFRAME_ID);
  const gameboxUrl = sessionStorage.getItem(GAMEBOX_SESSION_KEY);

  return {
    gdlibExists: !!gdlib,
    gdlibInitialized: !!gdlib?.isInitialized,
    moduleExists: !!gameboxModule,
    iframeExists: !!gameboxIframe,
    urlExists: !!gameboxUrl,
    isReady: !!gameboxModule?.isGameBoxReady,
  };
};

// Utility: Log diagnostics
const logDiagnostics = (diagnostics: IGameBoxDiagnostics, retryCount: number): void => {
  console.log(`[GameBox] ========== Retry attempt #${retryCount} ==========`);
  console.log('[GameBox] GDLib exists:', diagnostics.gdlibExists);
  console.log('[GameBox] GDLib initialized:', diagnostics.gdlibInitialized);
  console.log('[GameBox] Module exists:', diagnostics.moduleExists);
  console.log('[GameBox] Iframe exists:', diagnostics.iframeExists);
  console.log('[GameBox] URL in session:', diagnostics.urlExists);
  console.log('[GameBox] Ready:', diagnostics.isReady);
};

// Utility: Wait for GameBox ready with timeout and polling
const waitForGameBoxReady = (retryCount: number): Promise<boolean> => {
  return new Promise((resolve) => {
    const gdlib = window.__gdlib;

    // Check if already ready
    if (gdlib?.[GAMEBOX_MODULE_KEY]?.isGameBoxReady) {
      console.log('[GameBox] Already ready!');
      resolve(true);
      return;
    }

    const callbackId = `gamebox_retry_${retryCount}_${Date.now()}`;

    // Cleanup function
    const cleanup = (): void => {
      clearTimeout(timeoutId);
      clearInterval(pollIntervalId);
      gdlib?.unRegisterCallBack?.('gameBoxReady', callbackId);
    };

    // Setup timeout
    const timeoutId: NodeJS.Timeout = setTimeout(() => {
      console.warn('[GameBox] ⏱ Timeout waiting for ready event');
      cleanup();
      resolve(false);
    }, GAMEBOX_READY_TIMEOUT);

    // Register callback for gameBoxReady event
    gdlib?.registerCallBack?.('gameBoxReady', callbackId, () => {
      console.log('[GameBox] ✓ Ready event received!');
      cleanup();
      resolve(true);
    });

    // Polling as backup mechanism
    let pollAttempts = 0;
    const pollIntervalId: NodeJS.Timeout = setInterval(() => {
      pollAttempts++;
      const isNowReady = window.__gdlib?.[GAMEBOX_MODULE_KEY]?.isGameBoxReady;

      if (isNowReady) {
        console.log(`[GameBox] ✓ Ready detected via polling (attempt ${pollAttempts})`);
        cleanup();
        resolve(true);
      } else if (pollAttempts >= GAMEBOX_MAX_POLL_ATTEMPTS) {
        clearInterval(pollIntervalId);
      }
    }, GAMEBOX_POLL_INTERVAL);
  });
};

// Utility: Trigger manual initialization
const triggerManualInit = async (diagnostics: IGameBoxDiagnostics, retryCount: number): Promise<void> => {
  if (!diagnostics.iframeExists && diagnostics.urlExists) {
    console.log('[GameBox] Iframe missing but URL exists. Dispatching retry init event...');

    window.dispatchEvent(
      new CustomEvent('gamebox-retry-init', {
        detail: { url: diagnostics.urlExists, retryCount },
      })
    );

    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
};

// Utility: Create handler to suppress Status: 0 errors
const createStatus0ErrorHandler = (): ((event: PromiseRejectionEvent) => void) => {
  return (event: PromiseRejectionEvent): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reason: any = event.reason;
    const errorMessage = String(reason?.message || reason || '');
    const errorStatus = reason?.status;
    const errorString = String(reason || '');

    // Suppress Status: 0 errors (expected when re-initializing)
    if (
      errorMessage.includes('Status: 0') ||
      errorMessage.includes('Status 0') ||
      errorString.includes('Status: 0') ||
      errorString.includes('Status 0') ||
      errorStatus === 0
    ) {
      console.warn('[GameBox] ⚠ Suppressed unhandled Status 0 error (expected during re-init)');
      event.preventDefault();
    }
  };
};

// Utility: Handle GDLib re-initialization when iframe/URL is missing
const handleGDLibReInitialization = async (initializeGDLib: (force?: boolean) => Promise<void>): Promise<boolean> => {
  console.log('[GameBox] ⚠ GDLib initialized but iframe/URL missing. Re-initializing to trigger getPublicGameUrl...');
  console.log('[GameBox] Note: Status 0 error may appear but is non-critical (duplicate init request)');

  const unhandledRejectionHandler = createStatus0ErrorHandler();
  window.addEventListener('unhandledrejection', unhandledRejectionHandler);

  try {
    await initializeGDLib(true);
    console.log('[GameBox] ✓ GDLib re-initialized successfully');
  } catch (error: any) {
    if (error?.message?.includes('Status: 0') || error?.status === 0) {
      console.warn('[GameBox] ⚠ Status 0 error caught (non-critical, continuing)');
    } else {
      console.error('[GameBox] ✗ Re-initialization failed with error:', error?.message || error);
      window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
      return false;
    }
  } finally {
    setTimeout(() => {
      window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
    }, 2000);
  }

  await new Promise((resolve) => setTimeout(resolve, 1500));

  const updatedDiagnostics = getGameBoxDiagnostics();
  if (updatedDiagnostics.urlExists || updatedDiagnostics.iframeExists) {
    console.log('[GameBox] ✓ Re-initialization succeeded - URL/iframe created');
    return true;
  }

  console.warn('[GameBox] ⚠ Re-initialization completed but URL/iframe still missing');
  return true;
};

// Utility: Ensure GDLib is initialized and setup properly
const ensureGDLibInitialized = async (
  diagnostics: IGameBoxDiagnostics,
  initializeGDLib?: (force?: boolean) => Promise<void>
): Promise<boolean> => {
  if (!initializeGDLib) {
    console.error('[GameBox] ✗ No initialization function provided');
    return false;
  }

  if (!diagnostics.gdlibInitialized) {
    console.log('[GameBox] ⚠ GDLib not initialized. Attempting to initialize...');
    try {
      await initializeGDLib(false);
      console.log('[GameBox] ✓ GDLib initialized successfully');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return true;
    } catch (error) {
      console.error('[GameBox] ✗ Error during GDLib initialization:', error);
      return false;
    }
  }

  if (diagnostics.gdlibInitialized && (!diagnostics.iframeExists || !diagnostics.urlExists)) {
    return await handleGDLibReInitialization(initializeGDLib);
  }

  console.log('[GameBox] ✓ GDLib already initialized');
  return true;
};

// Main Hook
export const useGameBoxReady = (options: IUseGameBoxReadyOptions): IUseGameBoxReadyResult => {
  const { onSuccess, onError, getBaseDescription, initializeGDLib } = options;

  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [status, setStatus] = useState<GameBoxStatus>('loading');
  const isProcessingRef = useRef(false);

  // Check if GameBox is ready
  const checkReadiness = useCallback((): boolean => {
    const diagnostics = getGameBoxDiagnostics();
    return diagnostics.isReady;
  }, []);

  // Handle retry logic
  const handleRetry = useCallback(async (): Promise<void> => {
    if (isProcessingRef.current) {
      console.log('[GameBox] Already processing, skipping...');
      return;
    }

    isProcessingRef.current = true;
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    setStatus('loading');

    try {
      // Get diagnostics
      const diagnostics = getGameBoxDiagnostics();
      logDiagnostics(diagnostics, newRetryCount);

      // Early exit if already ready
      if (diagnostics.isReady) {
        console.log('[GameBox] ✓ Already ready!');
        setIsPopUpOpen(false);
        setRetryCount(0);
        setStatus('loading');
        onSuccess?.();
        return;
      }

      // Ensure GDLib is initialized
      const isInitialized = await ensureGDLibInitialized(diagnostics, initializeGDLib);
      if (!isInitialized) {
        setStatus('error');
        onError?.();
        return;
      }

      // Check if URL exists
      if (!diagnostics.urlExists) {
        console.error('[GameBox] ✗ Module URL not fetched yet');
        setStatus('error');
        onError?.();
        return;
      }

      // Try manual initialization if needed
      await triggerManualInit(diagnostics, newRetryCount);

      // Wait for ready status
      const isReady = await waitForGameBoxReady(newRetryCount);

      if (isReady) {
        console.log('[GameBox] ✓ Ready! Executing success callback...');
        setIsPopUpOpen(false);
        setRetryCount(0);
        setStatus('loading');
        onSuccess?.();
      } else {
        console.warn(`[GameBox] ✗ Still not ready after attempt #${newRetryCount}`);
        setStatus('timeout');

        // Log additional help after multiple attempts
        if (newRetryCount >= 3) {
          console.error('[GameBox] Multiple attempts failed. Please refresh or contact support.');
        }

        onError?.();
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [retryCount, onSuccess, onError, initializeGDLib]);

  // Handle close
  const handleClose = useCallback((): void => {
    setIsPopUpOpen(false);
    setRetryCount(0);
    setStatus('loading');
  }, []);

  // Show popup
  const showPopup = useCallback((): void => {
    setIsPopUpOpen(true);
  }, []);

  // Generate description
  const description = useCallback((): string => {
    let desc = getBaseDescription();

    // Only add retry count if there were attempts
    if (retryCount > 0) {
      desc += `<br/><br/><strong>Retry attempt #${retryCount}</strong>`;
    }

    return desc;
  }, [retryCount, getBaseDescription]);

  return {
    isPopUpOpen,
    retryCount,
    status,
    description: description(),
    handleRetry,
    handleClose,
    checkReadiness,
    showPopup,
  };
};
